import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchAdSyncStatus, fetchAdAccounts, updateAdAccount, startAdSync,
    fetchAdSyncBatch, fetchAdSyncRuns, fetchCampaigns,
} from '../../api';
import { useAccess } from '../auth/AccessContext';
import Modal from './Modal';
import DateRangePicker from '../common/DateRangePicker';
import { formatMoney, toISODate } from './shared';
import '../reports/Reports.css';
import './Marketing.css';
import '../canopy/Canopy.css';

// Ad Spend Sync: daily spend pulled from Google Ads and Meta into the Spends
// table as the actual amount, one campaign per ad account. The server runs it
// every night; Sync now runs the same thing on demand, and a backfill range
// can reach further back. Platform numbers replace whatever was typed on the
// days a run covers.
//
// A run happens on the server in the background, so this page starts it and
// polls until every account has an outcome. Leaving the page doesn't stop it.

const POLL_EVERY_MS = 3000;

const PLATFORM_LABEL = { google_ads: 'Google Ads', meta: 'Meta' };

const STATUS_LABEL = { queued: 'Waiting', running: 'Pulling…', success: 'Done', failed: 'Failed' };

const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${toISODate(d)} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

// Shows an account id the way its platform's own UI does.
const formatAccountId = (platform, id) => {
    if (platform === 'google_ads' && /^\d{10}$/.test(id)) return `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}`;
    if (platform === 'meta') return `act_${id}`;
    return id;
};

const formatHour = (hour, minute) => {
    const d = new Date(2000, 0, 1, hour, minute);
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

function StatusPill({ status }) {
    return <span className={`canopy__pill canopy__pill--${status}`}>{STATUS_LABEL[status] || status}</span>;
}

// A run's outcome in one line: the money and days, or why it failed.
function RunDetail({ run }) {
    if (!run) return null;
    if (run.status === 'failed') return run.error || 'Failed';
    if (run.status !== 'success') return null;
    const days = run.days_written ?? 0;
    return <>{formatMoney(run.total)} over {days} day{days === 1 ? '' : 's'}</>;
}

const runRange = (run) => (run.start_date === run.end_date ? run.start_date : `${run.start_date} to ${run.end_date}`);

export default function AdSync() {
    const { vendorScope } = useAccess();
    const [status, setStatus] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [campaigns, setCampaigns] = useState(null); // null: couldn't load (no Campaigns access)
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // idle | starting | running | done
    const [phase, setPhase] = useState('idle');
    const [batch, setBatch] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [backfill, setBackfill] = useState(false);
    const [range, setRange] = useState({ startDate: '', endDate: '' });
    const [savingAccount, setSavingAccount] = useState(null);

    const unmounted = useRef(false);
    useEffect(() => () => { unmounted.current = true; }, []);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [st, accts, history] = await Promise.all([fetchAdSyncStatus(), fetchAdAccounts(), fetchAdSyncRuns()]);
            setStatus(st);
            setAccounts(accts || []);
            setRuns(history || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Campaigns are only needed to re-point an account; a profile without the
    // Campaigns page still sees which campaign each account feeds, by name.
    useEffect(() => {
        fetchCampaigns().then((data) => setCampaigns(data || [])).catch(() => setCampaigns(null));
    }, []);

    const campaignsByVendor = useMemo(() => {
        const map = {};
        for (const c of campaigns || []) (map[c.vendor_id] = map[c.vendor_id] || []).push(c);
        return map;
    }, [campaigns]);

    const watchBatch = useCallback(async (batchId) => {
        while (!unmounted.current) {
            await new Promise((r) => setTimeout(r, POLL_EVERY_MS));
            let res;
            try { res = await fetchAdSyncBatch(batchId); } catch { continue; }
            if (unmounted.current) return;
            setBatch(res);
            if (res.done) {
                setPhase('done');
                load();
                return;
            }
        }
    }, [load]);

    const openConfirm = () => {
        setBackfill(false);
        setRange({ startDate: status?.default_start || '', endDate: status?.default_end || '' });
        setConfirming(true);
    };

    const backfillError = useMemo(() => {
        if (!backfill) return null;
        const { startDate, endDate } = range;
        if (!startDate || !endDate) return 'Enter a start and end date.';
        if (endDate < startDate) return "The end date can't be before the start date.";
        if (status?.today && endDate > status.today) return "The end date can't be in the future.";
        return null;
    }, [backfill, range, status]);

    const handleStart = async () => {
        setConfirming(false);
        setError(null);
        setNotice(null);
        setPhase('starting');
        try {
            const res = await startAdSync(backfill ? { start_date: range.startDate, end_date: range.endDate } : {});
            setBatch(res);
            if (res.done) {
                setPhase('done');
                setNotice('No enabled accounts to sync.');
                load();
                return;
            }
            setPhase('running');
            load();
            watchBatch(res.batch_id);
        } catch (err) {
            setError(err.message);
            setPhase('idle');
        }
    };

    const saveAccount = async (account, change) => {
        setSavingAccount(account.id);
        setError(null);
        try {
            const updated = await updateAdAccount(account.id, change);
            setAccounts((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingAccount(null);
        }
    };

    const configured = Boolean(status?.configured);
    const busy = phase === 'starting' || phase === 'running';
    const unconfigured = (status?.platforms || []).filter((p) => !p.configured);
    const batchRuns = batch?.runs || [];
    const counts = batchRuns.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});
    const finished = batchRuns.filter((r) => r.status === 'success' || r.status === 'failed').length;

    // Accounts the server is configured for but hasn't synced yet have no row.
    const pending = (status?.platforms || []).flatMap((p) => p.account_ids
        .filter((id) => !accounts.some((a) => a.platform === p.platform && a.account_id === id))
        .map((id) => ({ platform: p.platform, id })));

    const schedule = status?.schedule;
    const windowDays = status?.window_days || 14;

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Ad Spend Sync</h1>
                    <p className="page__subtitle">
                        Daily spend from Google Ads and Meta, written to Spends as the actual amount on one campaign
                        per ad account.{' '}
                        {schedule
                            ? <>Every night at {formatHour(schedule.hour, schedule.minute)} ({schedule.timezone}) the last {windowDays} days are pulled again. </>
                            : <>There is no nightly run on this server, so use Sync now. </>}
                        The platform's numbers replace anything typed on those days.
                    </p>
                </div>
                {configured && !vendorScope.scoped && (
                    <div className="mkt__header-actions">
                        <button className="blueButton" onClick={openConfirm} disabled={busy}>
                            {phase === 'starting' ? 'Starting…' : busy ? 'Syncing…' : 'Sync now'}
                        </button>
                    </div>
                )}
            </div>

            {loading && <p className="page__subtitle">Loading…</p>}
            {error && <p className="mkt__error">{error}</p>}
            {notice && <p className="mkt__success">{notice}</p>}

            {status && !configured && (
                <p className="mkt__preview mkt__preview--warn">
                    Neither Google Ads nor Meta is configured on this server, so nothing syncs. An admin needs to set
                    the GOOGLE_ADS_* or META_* settings.
                </p>
            )}
            {configured && unconfigured.length > 0 && (
                <p className="mkt__preview mkt__preview--warn">
                    {unconfigured.map((p) => p.label).join(' and ')} {unconfigured.length === 1 ? 'is' : 'are'} not
                    configured on this server and won't sync.
                </p>
            )}
            {status?.running && phase === 'idle' && (
                <p className="canopy__progress">A sync is running right now. Its results will appear in the history below.</p>
            )}
            {vendorScope.scoped && configured && (
                <p className="page__subtitle">
                    Your profile is limited to specific vendors, so it can see these accounts but not run a sync or re-point them.
                </p>
            )}

            {batch && (phase === 'running' || phase === 'done') && batchRuns.length > 0 && (
                <section className="mkt__panel canopy__preview">
                    <h2 className="mkt__panel-title">
                        {phase === 'done' ? 'Sync finished' : `Syncing… ${finished} of ${batchRuns.length} accounts done`}
                    </h2>
                    {phase === 'done' && (
                        <p className={counts.failed ? 'mkt__preview mkt__preview--warn' : 'mkt__success'}>
                            {counts.success || 0} account{counts.success === 1 ? '' : 's'} synced
                            {counts.failed ? `, ${counts.failed} failed` : ''}.
                        </p>
                    )}
                    <div className="canopy__table-wrap">
                        <table className="canopy__table">
                            <thead>
                                <tr><th>Platform</th><th>Account</th><th>Days</th><th>Status</th><th>Detail</th></tr>
                            </thead>
                            <tbody>
                                {batchRuns.map((r) => (
                                    <tr key={r.id}>
                                        <td>{PLATFORM_LABEL[r.platform] || r.platform}</td>
                                        <td>{formatAccountId(r.platform, r.account_id)}</td>
                                        <td>{runRange(r)}</td>
                                        <td><StatusPill status={r.status} /></td>
                                        <td className="canopy__detail"><RunDetail run={r} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* ── Accounts ────────────────────────────────────── */}
            {status && (
                <section className="mkt__panel canopy__preview" style={{ marginTop: 16 }}>
                    <h2 className="mkt__panel-title">Accounts</h2>
                    <p className="mkt__panel-hint">
                        Each account's spend lands on one campaign, created for it on the first sync. Pick a different
                        campaign to send future syncs there; spend already written stays where it is. Turn an account
                        off to stop syncing it without removing its history.
                    </p>
                    {accounts.length === 0 && pending.length === 0 && (
                        <p className="page__subtitle">No ad accounts yet.</p>
                    )}
                    {(accounts.length > 0 || pending.length > 0) && (
                        <div className="canopy__table-wrap">
                            <table className="canopy__table">
                                <thead>
                                    <tr>
                                        <th>Platform</th><th>Account</th><th>Feeds campaign</th><th>Sync</th><th>Last run</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map((a) => {
                                        const options = campaignsByVendor[a.vendor_id] || [];
                                        const canEdit = campaigns !== null && !vendorScope.scoped;
                                        return (
                                            <tr key={a.id}>
                                                <td>{PLATFORM_LABEL[a.platform] || a.platform}</td>
                                                <td>
                                                    {formatAccountId(a.platform, a.account_id)}
                                                    {a.name && <div className="canopy__detail">{a.name}</div>}
                                                    {!a.in_config && (
                                                        <div className="canopy__warn-inline">Not in this server's settings, so it won't sync.</div>
                                                    )}
                                                </td>
                                                <td>
                                                    {canEdit ? (
                                                        <select
                                                            value={a.campaign_id}
                                                            disabled={savingAccount === a.id}
                                                            onChange={(e) => saveAccount(a, { campaign_id: Number(e.target.value) })}
                                                        >
                                                            {!options.some((c) => c.id === a.campaign_id) && (
                                                                <option value={a.campaign_id}>{a.campaign_name}</option>
                                                            )}
                                                            {options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                        </select>
                                                    ) : a.campaign_name}
                                                    <div className="canopy__detail">{a.vendor_name}</div>
                                                </td>
                                                <td>
                                                    <label className="mkt__check">
                                                        <input
                                                            type="checkbox"
                                                            checked={a.enabled}
                                                            disabled={vendorScope.scoped || savingAccount === a.id}
                                                            onChange={(e) => saveAccount(a, { enabled: e.target.checked })}
                                                        />
                                                        {a.enabled ? 'On' : 'Off'}
                                                    </label>
                                                </td>
                                                <td className="canopy__detail">
                                                    {a.last_run ? (
                                                        <>
                                                            <StatusPill status={a.last_run.status} />{' '}
                                                            {formatDateTime(a.last_run.finished_at || a.last_run.created_at)}
                                                            <div><RunDetail run={a.last_run} /></div>
                                                        </>
                                                    ) : <em>Never</em>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {pending.map((p) => (
                                        <tr key={`${p.platform}-${p.id}`}>
                                            <td>{PLATFORM_LABEL[p.platform] || p.platform}</td>
                                            <td>{formatAccountId(p.platform, p.id)}</td>
                                            <td colSpan={3} className="canopy__detail">
                                                <em>Set up on the first sync: a campaign named “{PLATFORM_LABEL[p.platform]} {formatAccountId(p.platform, p.id)}” is created under the {PLATFORM_LABEL[p.platform]} vendor.</em>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {/* ── History ─────────────────────────────────────── */}
            <section className="canopy__history">
                <h2 className="mkt__panel-title">Recent runs</h2>
                {!loading && runs.length === 0 && <p className="page__subtitle">No syncs have run yet.</p>}
                {runs.length > 0 && (
                    <div className="canopy__table-wrap">
                        <table className="canopy__table">
                            <thead>
                                <tr>
                                    <th>When</th><th>Platform</th><th>Account</th><th>Days</th><th>By</th><th>Status</th><th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r) => (
                                    <tr key={r.id}>
                                        <td>{formatDateTime(r.created_at)}</td>
                                        <td>{PLATFORM_LABEL[r.platform] || r.platform}</td>
                                        <td>{formatAccountId(r.platform, r.account_id)}</td>
                                        <td>{runRange(r)}</td>
                                        <td>{r.requested_by === 'scheduler' ? 'Nightly' : r.requested_by}</td>
                                        <td><StatusPill status={r.status} /></td>
                                        <td className="canopy__detail"><RunDetail run={r} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {confirming && (
                <Modal
                    title="Sync ad spend now?"
                    onClose={() => setConfirming(false)}
                    onSubmit={handleStart}
                    submitLabel="Sync now"
                    submitDisabled={Boolean(backfillError)}
                    error={backfillError}
                >
                    <p className="mkt-modal__text">
                        This pulls{' '}
                        {backfill
                            ? <><strong>{range.startDate}</strong> to <strong>{range.endDate}</strong></>
                            : <>the last <strong>{windowDays} days</strong> ({status?.default_start} to {status?.default_end})</>}
                        {' '}from every enabled account and replaces the actual amount on those days, including
                        anything typed by hand. Estimates are left alone.
                    </p>
                    <label className="mkt__check">
                        <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />
                        Choose the dates (backfill)
                    </label>
                    {backfill && (
                        <div className="mkt__controls">
                            <div className="mkt__field">
                                <span>Dates</span>
                                <DateRangePicker value={range} onChange={setRange} max={status?.today} />
                            </div>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}
